import { Command } from 'commander'
import { execa } from 'execa'
import fs from 'fs-extra'
import path from 'path'
import prompts from 'prompts'
import { z } from 'zod'

import { preflightAdd } from '@preflights/preFlightAdd'
import { txt } from '@utils/colors'
import { getPackageManager } from '@utils/getPackageManager'
import { ProjectInfo, getProjectType } from '@utils/getProjectInfo'
import { handleError } from '@utils/handleError'
import { logger } from '@utils/logger'
import { type RegistryComponents, getRegistryComponents } from '@utils/registry'
import { spinner } from '@utils/spinner'

const addOptionsSchema = z.object({
  components: z.array(z.string()).optional(),
  cwd: z.string(),
  yes: z.boolean(),
  all: z.boolean(),
  overwrite: z.boolean(),
  silent: z.boolean(),
})

export type AddOptions = z.infer<typeof addOptionsSchema>

// Define the `add` command with available options
export const add = new Command()
  .name('add')
  .description('Add components to ui folder inside the components folder in your project')
  .argument('[components...]', 'The components to add.')
  .option('-y, --yes', 'Skip confirmation prompts.', false)
  .option('-a, --all', 'Install all available components.', false)
  .option('-c, --cwd <cwd>', 'The working directory. Defaults to the current directory.', process.cwd())
  .option('-o, --overwrite', 'Overwrite existing files if they exist.', false)
  .option('-s, --silent', 'Mute output logs.', false)
  .action(async (components, opts) => {
    try {
      const options = addOptionsSchema.parse({
        components,
        cwd: path.resolve(opts.cwd),
        ...opts,
      })

      await preflightAdd(options)

      // If no components were provided, prompt the user to select
      if (!options.components?.length) {
        const selectedComponents = await promptForComponents(options)
        if (!selectedComponents.length) {
          logger.warn('No components selected. Exiting.')
          process.exit(1)
        }
        options.components = selectedComponents
      }

      const validComponents = await checkComponentsAvailability(options.components)

      const resolvedComponents = await resolveComponents(await getRegistryComponents(), validComponents)

      await addComponentsToProject(resolvedComponents, options, await getProjectType(options.cwd))
    } catch (error) {
      handleError(error)
    }
  })

/**
 * Prompts the user to select components if they were not provided via CLI.
 *
 * @param options - The parsed command options.
 * @returns A promise resolving to an array of selected component names.
 */
async function promptForComponents(options: AddOptions): Promise<string[]> {
  try {
    const availableComponents = (await getRegistryComponents()).map((components) => components.name)

    if (options.all) {
      return availableComponents
    }

    if (options.components?.length) {
      return options.components
    }

    const { components } = await prompts({
      type: 'multiselect',
      name: 'components',
      message: 'Which components would you like to add?',
      hint: 'Space to select. A to toggle all. Enter to submit.',
      instructions: false,
      choices: availableComponents.map((component) => ({
        title: component,
        value: component,
        selected: options.components?.includes(component),
      })),
    })
    return components
  } catch (error) {
    handleError(new Error('Failed to fetch available components.'))
  }
}

/**
 * Checks if the selected components exist in the registry.
 *
 * @param components - List of component names to validate.
 * @returns A promise resolving to an array of valid component names.
 */
const checkComponentsAvailability = async (components: string[]): Promise<string[]> => {
  const componentAvailabilitySpinner = spinner('Checking registry').start()
  const availableComponents = (await getRegistryComponents()).map((components) => components.name)

  const invalidComponents = components.filter((component) => !availableComponents.includes(component))

  if (invalidComponents.length > 0) {
    componentAvailabilitySpinner.fail(`Checking registry. Not found:`)
    invalidComponents.forEach((component) => logger.info(`- ${component}`))
    process.exit(1)
  }

  componentAvailabilitySpinner.succeed()
  return components
}

/**
 * Adds the resolved components to the project, handling file creation, overwriting, and logging.
 *
 * @param resolvedComponents - The resolved components with their file definitions.
 * @param options - The parsed command options.
 * @param config - The project configuration (e.g., whether it has a `src` directory).
 */
async function addComponentsToProject(
  resolvedComponents: RegistryComponents,
  options: AddOptions,
  config: ProjectInfo
): Promise<void> {
  const hasSrcDir = config.hasSrcDir

  await installComponentDependencies(resolvedComponents, options)

  const filesCreated: string[] = []
  const filesUpdated: string[] = []
  const filesSkipped: string[] = []

  for (const component of resolvedComponents) {
    for (const file of component.files) {
      const filePath = path.join(options.cwd, hasSrcDir ? 'src' : '', file.dir, file.name)
      const fileExists = await fs.exists(filePath)
      if (fileExists && !options.overwrite) {
        const { overwrite } = await prompts({
          type: 'confirm',
          name: 'overwrite',
          message: `Component ${txt.info(file.name)} already exists.Would you like to overwrite?`,
          initial: false,
        })

        if (!overwrite) {
          filesSkipped.push(filePath)
          continue
        }
      }

      const dirPath = path.dirname(filePath)
      if (!(await fs.exists(dirPath))) {
        await fs.mkdir(dirPath, { recursive: true })
      }

      await fs.writeFile(filePath, file.content)

      if (fileExists) {
        filesUpdated.push(filePath)
      } else {
        filesCreated.push(filePath)
      }
    }
  }

  if (!options.silent) {
    if (filesCreated.length > 0) {
      logger.break()
      logger.info(`Created ${filesCreated.length} file(s):`)
      filesCreated.forEach((file) => logger.log(`  - ${file}`))
    }
    if (filesUpdated.length > 0) {
      logger.break()
      logger.info(`Updated ${filesUpdated.length} file(s):`)
      filesUpdated.forEach((file) => logger.log(`  - ${file}`))
    }
    if (filesSkipped.length > 0) {
      logger.break()
      logger.info(`Skipped ${filesSkipped.length} file(s):`)
      filesSkipped.forEach((file) => logger.log(`  - ${file}`))
    }
  }
}

/**
 * Installs the required dependencies for the selected components.
 *
 * @param resolvedComponents - The resolved components with their dependencies.
 * @param options - The parsed command options.
 */
async function installComponentDependencies(
  resolvedComponents: RegistryComponents,
  options: AddOptions
): Promise<void> {
  const dependencies = new Set<string>()

  for (const component of resolvedComponents) {
    if (component.dependencies?.length) {
      component.dependencies.forEach((dep) => dependencies.add(dep))
    }
  }

  if (!dependencies.size) {
    return
  }

  const dependencyList = Array.from(dependencies)

  try {
    const dependencySpinner = spinner('Installing the dependencies', {
      silent: options.silent,
    }).start()
    await execa((await getPackageManager(options.cwd)).name, ['install', '--legacy-peer-deps', ...dependencyList], {
      cwd: options.cwd,
      stdio: 'ignore',
    })

    dependencySpinner.succeed()
  } catch (error) {
    handleError(new Error(`Failed to install dependencies: ${error.message}`))
  }
}

/**
 * Resolves components and their registry dependencies recursively from the registry.
 *
 * @param registryComponents - All available components in the registry.
 * @param componentNames - The names of the components to resolve.
 * @returns A deduplicated array of components including dependencies.
 */
async function resolveComponents(registryComponents: RegistryComponents, componentNames: string[]) {
  const flattenedComponents: RegistryComponents = []

  for (const name of componentNames) {
    const componentEntry = registryComponents.find((entry) => entry.name === name)

    if (!componentEntry) {
      continue
    }

    flattenedComponents.push(componentEntry)

    if (componentEntry.registryDependencies) {
      const dependencies = await resolveComponents(registryComponents, componentEntry.registryDependencies)
      flattenedComponents.push(...dependencies)
    }
  }
  return flattenedComponents.filter(
    (component, index, self) => self.findIndex((c) => c.name === component.name) === index
  )
}
